import { PriorityBadge } from '@/components/badges/PriorityBadge'
import { RepairStatusBadge } from '@/components/badges/RepairStatusBadge'
import { SectionCard } from '@/components/SectionCard'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useDateFormatter } from '@/hooks/useDateFormatter'
import { useUI } from '@/hooks/useUI'
import { AlertTriangle, Calendar, Plus, User, Wrench } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useGetRepairs } from '../hooks/useRepair'
import { CreateRepairDialog } from './CreateRepairDialog'
import { RepairDetailDialog } from './RepairDetailDialog'

const priorityOrder: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 }

export function RepairsView() {
  const { t } = useTranslation()
  const [createOpen, setCreateOpen] = useState(false)
  const [selectedRepairId, setSelectedRepairId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const { repairStatuses } = useUI()
  const { formatDateTime } = useDateFormatter()

  const statusParam = statusFilter === 'all' ? undefined : statusFilter
  const { data: repairs = [], isLoading } = useGetRepairs({ status: statusParam })

  const sortedRepairs = useMemo(
    () => [...repairs].sort((a, b) => (priorityOrder[a.priority] ?? 99) - (priorityOrder[b.priority] ?? 99)),
    [repairs],
  )
  const openCount = repairs.filter(repair => repair.status === 'Open').length
  const activeCount = sortedRepairs.filter(repair => repair.status !== 'Completed' && repair.status !== 'Closed').length

  return (
    <>
      <SectionCard
        data-testid="repairs-list"
        header={
          <div className="flex items-center gap-3">
            <CardTitle className="text-sm font-medium text-foreground">
              {t('repairs.casesTitle', { count: activeCount })}
            </CardTitle>
            {openCount > 0 && (
              <Badge className="bg-warning/15 text-[10px] text-warning">
                {t('repairs.openBadge', { count: openCount })}
              </Badge>
            )}
          </div>
        }
        right={
          <>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="repairs-status-filter" className="h-8 w-[140px] bg-secondary/50 text-xs">
                <SelectValue placeholder={t('common.status')} />
              </SelectTrigger>
              <SelectContent className="bg-card text-foreground">
                <SelectItem value="all">{t('common.allStatuses')}</SelectItem>
                {repairStatuses?.map(status => (
                  <SelectItem key={status.value} value={status.value}>
                    {status.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button data-testid="repairs-create-button" size="sm" className="h-8 text-xs" onClick={() => setCreateOpen(true)}>
              <Plus className="mr-1 size-3" />
              {t('repairs.create')}
            </Button>
          </>
        }
      >
        {isLoading ? (
          <div className="flex items-center justify-center py-12 text-sm text-muted-foreground">
            {t('common.loading')}
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {sortedRepairs.map(repair => (
              <div
                key={repair.id}
                data-testid="repair-card"
                className="flex cursor-pointer flex-col gap-2 rounded-lg border border-border/30 bg-secondary/20 p-4 transition-colors hover:border-primary/30"
                onClick={() => {
                  setSelectedRepairId(repair.id)
                  setDetailOpen(true)
                }}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="text-xs font-medium text-foreground">{repair.title}</span>
                      <PriorityBadge priority={repair.priority} />
                      <RepairStatusBadge status={repair.status} />
                    </div>
                    <span className="text-xs text-muted-foreground">{repair.itemSerialNumber}</span>
                  </div>
                  <span className="shrink-0 text-[10px] text-muted-foreground">
                    {formatDateTime(repair.dateCreated, {
                      day: '2-digit',
                      month: 'short',
                      hour: '2-digit',
                      minute: '2-digit',
                    })}
                  </span>
                </div>
                <p className="line-clamp-1 text-xs text-muted-foreground">{repair.description}</p>
                <div className="flex flex-wrap items-center gap-4 text-[10px] text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="size-2.5" />
                    {repair.reporterName}
                  </span>
                  <span className="flex items-center gap-1">
                    <AlertTriangle className="size-2.5" />
                    {repair.reporterDepartmentName}
                  </span>
                  {repair.technicianName && (
                    <span className="flex items-center gap-1">
                      <Wrench className="size-2.5" />
                      {t('repairs.technician')}: {repair.technicianName}
                    </span>
                  )}
                  {repair.estimatedCompletion && (
                    <span className="flex items-center gap-1">
                      <Calendar className="size-2.5" />
                      {formatDateTime(repair.estimatedCompletion)}
                    </span>
                  )}
                </div>
              </div>
            ))}
            {sortedRepairs.length === 0 && (
              <div className="flex flex-col items-center py-12 text-center">
                <Wrench className="size-10 text-muted-foreground/30" />
                <p className="mt-3 text-xs text-muted-foreground">{t('repairs.noRepairs')}</p>
              </div>
            )}
          </div>
        )}
      </SectionCard>

      <CreateRepairDialog open={createOpen} onOpenChange={setCreateOpen} />
      <RepairDetailDialog repairId={selectedRepairId} open={detailOpen} onOpenChange={setDetailOpen} />
    </>
  )
}
